/**
 * Bilingual copy for the hub, transcribed from the reference project's
 * `src/lib/i18n/dictionaries.ts` (`gameMenu`, `games`, `common`) so our wording
 * is byte-identical to the approved build in BOTH languages. Nothing here is
 * translated by hand — a reworded string would silently change what the A/B
 * judge is comparing.
 *
 * Copy lives next to the scene rather than in a global i18n layer because the
 * hub is currently the only bilingual surface; when Game 1/2 grow their own,
 * this table is the obvious thing to hoist.
 */
import type { RouteId } from '../../core/types';
import { HEX } from '../../render/palette';

export type Lang = 'en' | 'zh';

export const LANGS: readonly Lang[] = ['en', 'zh'];

/** A string that exists in both languages. */
export type Bi = Readonly<Record<Lang, string>>;

export interface GameCardCopy {
  /** Stable key used for DOM ids and diorama lookup. */
  readonly key: 'home' | 'factory' | 'reloop';
  /** Where Play navigates. `null` means the game is not built yet. */
  readonly route: RouteId | null;
  /** Accent colour, echoing the reference card fields (green / red / gold). */
  readonly accent: number;
  /** Ink-on-accent or cream-on-accent, whichever passes contrast. */
  readonly accentText: number;
  readonly level: Bi;
  readonly duration: Bi;
  readonly name: Bi;
  readonly subtitle: Bi;
  readonly description: Bi;
}

export const CARDS: readonly GameCardCopy[] = [
  {
    key: 'home',
    route: 'game1',
    accent: HEX.recycleGreen,
    accentText: HEX.ink,
    level: { en: 'Level 1', zh: '第 1 關' },
    duration: { en: '3 min', zh: '3 分鐘' },
    name: { en: 'Recycling 101 – Exploring Household Recycling', zh: '回收101：家居回收找找看' },
    subtitle: { en: 'Top-down house map', zh: '俯瞰家居地圖' },
    description: {
      en: 'Walk through a 4x4 home map, pick up objects, and sort recyclables at the collection box.',
      zh: '在 4x4 家居地圖中行走、拾起物品，並把可回收物放到回收箱。',
    },
  },
  {
    key: 'factory',
    route: 'game2',
    accent: HEX.cocaColaRed,
    accentText: HEX.white,
    level: { en: 'Level 2', zh: '第 2 關' },
    duration: { en: '6 orders', zh: '6 張訂單' },
    name: { en: 'Be a Plastic Detective – The Formula Workshop', zh: '塑膠偵探：配方工作坊' },
    subtitle: { en: 'Design · build · test', zh: '設計・製作・測試' },
    description: {
      en: 'Turn customer needs into part recipes, build products, and watch each design face a real test.',
      zh: '把顧客需要轉化為零件配方，製作產品，並觀看每項設計接受實際測試。',
    },
  },
  {
    key: 'reloop',
    route: 'game3',
    accent: HEX.goldenYellow,
    accentText: HEX.ink,
    level: { en: 'Easy or Difficult', zh: '簡易或困難' },
    duration: { en: 'Open-ended', zh: '持續營運' },
    name: { en: 'The Bottle-to-Bottle Loop – Reborn Challenge', zh: '「樽樽不息」：循環再生大挑戰' },
    subtitle: { en: 'Circular-economy management', zh: '循環經濟營運' },
    description: {
      en: 'Balance collection, recycling, bottling, demand, cash, and public trust in one local bottle-to-bottle loop.',
      zh: '在一個本地樽到樽循環中，平衡回收、再造、入樽、需求、現金與公眾信任。',
    },
  },
] as const;

export const UI: Readonly<Record<string, Bi>> = {
  eyebrow: { en: 'Coca-Cola Academy', zh: '可口可樂學院' },
  title: { en: 'Choose Your Game', zh: '選擇遊戲' },
  menuLabel: { en: 'Game menu', zh: '遊戲選單' },
  back: { en: 'Back', zh: '返回' },
  logout: { en: 'Log out', zh: '登出' },
  language: { en: 'Language', zh: '語言' },
  play: { en: 'Play', zh: '開始' },
  comingSoon: { en: 'Coming soon', zh: '即將推出' },
  hint: {
    en: 'Arrow keys browse · Enter plays',
    zh: '方向鍵瀏覽 · 按 Enter 開始',
  },
  previous: { en: 'Previous game', zh: '上一個遊戲' },
  next: { en: 'Next game', zh: '下一個遊戲' },
  /** Announced when the focused diorama changes, so screen readers keep up. */
  showing: { en: 'Showing', zh: '現正顯示' },
} as const;

/** The label the language button shows — always the language you'd switch TO. */
export const SWITCH_TO: Readonly<Record<Lang, string>> = {
  en: '繁中',
  zh: 'EN',
} as const;

/** `lang` attribute values, so the browser picks correct CJK font fallbacks. */
export const HTML_LANG: Readonly<Record<Lang, string>> = {
  en: 'en',
  zh: 'zh-HK',
} as const;

const STORAGE_KEY = 'cca.lang';

export function loadLang(): Lang {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === 'en' || raw === 'zh') return raw;
  } catch {
    /* Private-mode Safari throws on localStorage access; English is a fine default. */
  }
  return document.documentElement.lang.toLowerCase().startsWith('zh') ? 'zh' : 'en';
}

export function saveLang(lang: Lang): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, lang);
  } catch {
    /* Non-fatal: the toggle still works for this session. */
  }
}
